import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDeviceStatus } from "../../services/deviceStatus";
import {
  cookingFromAlarms,
  blockedFromAlarms,
  resolveDisplayState,
  firmwareLedColorHex,
  formatTemp,
  PcoState,
  DisplayState,
  TempUnit,
} from "../../services/cookingState";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import LockIcon from "@mui/icons-material/Lock";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import FireplaceIcon from "@mui/icons-material/Fireplace";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import PowerIcon from "@mui/icons-material/Power";
import WifiIcon from "@mui/icons-material/Wifi";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  formatOfflineDuration,
  formatOfflineSinceTooltip,
} from "../../services/offlineFormat";

type Props = {
  sensorName: string;
  isOnline: boolean;
  pcoState: PcoState;
  unit?: TempUnit;
  offlineSinceTs?: number | null;
  /** Hourly-path blocked flag (from alarms_v2). ORed with the live
   * device-status flag, not used as a fallback -- see resolvedBlocked. The pill
   *  still shows even if the live device-status reply omits camera_obstructed. */
  blocked?: boolean;
};

// Fallback accent colors; cooking/cooling override with the firmware LED color.
const COLORS = {
  offline: "#7C4DFF", // deep purple
  blocked: "#B00020", // critical red
  off: "#B00020", // dark red
  lock: "#1976D2", // blue
  cookingFallback: "#F1C40F", // yellow
  ovenOn: "#FFC107", // amber
  coolingFallback: "#FB8C00", // orange
  on: "#388E3C", // green
  online: "#388E3C", // green (same as stove power enabled)
  unknown: "#607D8B", // blue-grey
};

function meta(
  state: DisplayState,
  tempC: number | null,
  unit: TempUnit
): { label: string; color: string; Icon: typeof WifiIcon } {
  const tempStr = tempC != null ? formatTemp(tempC, unit) : null;
  switch (state) {
    case "offline":
      return { label: "Sensor offline", color: COLORS.offline, Icon: WifiOffIcon };
    case "blocked":
      return {
        label: "Sensor blocked",
        color: COLORS.blocked,
        Icon: VisibilityOffIcon,
      };
    case "off":
      return {
        label: "Cooktop Disabled",
        color: COLORS.off,
        Icon: PowerOffIcon,
      };
    case "lock":
      return { label: "Cooktop locked", color: COLORS.lock, Icon: LockIcon };
    case "cooking":
      return {
        label: tempStr ? `Cooking · ${tempStr}` : "Cooking",
        color: tempC != null ? firmwareLedColorHex(tempC) : COLORS.cookingFallback,
        Icon: LocalFireDepartmentIcon,
      };
    case "ovenOn":
      return { label: "Oven On", color: COLORS.ovenOn, Icon: FireplaceIcon };
    case "cooling":
      return {
        label: tempStr ? `Cooling · ${tempStr}` : "Cooling",
        color: tempC != null ? firmwareLedColorHex(tempC) : COLORS.coolingFallback,
        Icon: AcUnitIcon,
      };
    case "on":
      return { label: "Cooktop Enabled", color: COLORS.on, Icon: PowerIcon };
    case "online":
      return { label: "Sensor online", color: COLORS.online, Icon: WifiIcon };
    default:
      return { label: "Unknown", color: COLORS.unknown, Icon: HelpOutlineIcon };
  }
}

export default function StoveStateBadge({
  sensorName,
  isOnline,
  pcoState,
  unit = "F",
  offlineSinceTs = null,
  blocked = false,
}: Props) {
  // Only probe the device when it's reachable; offline short-circuits the
  // resolved state and needs no MQTT round-trip.
  const { data, isFetching } = useQuery({
    queryKey: ["device-status", sensorName],
    queryFn: () => getDeviceStatus(sensorName),
    enabled: isOnline,
    staleTime: 30_000,
    refetchInterval: isOnline ? 30_000 : false,
    retry: false,
  });

  const alarms = data?.alarms ?? null;
  const hottestTempC = data?.hottestTempC ?? null;
  const stovePoweredOn = data?.stovePoweredOn ?? null;
  const isCooking = isOnline ? cookingFromAlarms(alarms, pcoState) : null;
  // Live blocked from the device-status reply; fall back to the hourly alarms_v2
  // flag so the pill still shows if the live reply omits camera_obstructed.
  // EITHER source is enough -- deliberately an OR, not a fallback, and the
  // comment on the `blocked` prop used to say "fallback" while the code said
  // this. The two differ in exactly one case: live says not-blocked, hourly says
  // blocked. Then the pill stays Blocked and the fresher reading cannot clear it.
  //
  // Kept that way on purpose. A blind sensor cannot see the cooktop, so
  // over-reporting costs a manager a wasted check and under-reporting costs them
  // the signal entirely -- the same asymmetry blockedFromAlarms already applies
  // when it treats a missing field as false. The stale window is bounded: the
  // hourly path reads the newest record's whole currentAlarms set, and
  // un-blocking is itself a transition that clears it.
  const resolvedBlocked = (isOnline && blockedFromAlarms(alarms)) || blocked;

  const state = resolveDisplayState(
    isOnline,
    pcoState,
    isCooking,
    hottestTempC,
    stovePoweredOn,
    resolvedBlocked
  );
  const { label, color, Icon } = meta(state, hottestTempC, unit);
  const offlineSuffix =
    state === "offline" && offlineSinceTs
      ? ` · ${formatOfflineDuration(offlineSinceTs)}`
      : "";
  const tooltip =
    state === "offline" && offlineSinceTs
      ? formatOfflineSinceTooltip(offlineSinceTs)
      : isOnline && isFetching && !data
        ? "Checking live cooktop state…"
        : undefined;

  return (
    <span
      className="inline-flex items-center gap-1.5 font-medium text-sm whitespace-nowrap"
      title={tooltip}
    >
      <Icon fontSize="small" style={{ color }} />
      {label}
      {offlineSuffix}
    </span>
  );
}
